import React from 'react';
import { TEXT, VERDICTS, VERDICT_ORDER } from '../../config/listingPerformanceLabels';

/**
 * «أداء الوحدات» — the Monday glance: how many units sit on each verdict this week, the ones that need a
 * look first. Each count is a filter: press «ضعف الظهور 2» and the board shows those two units only;
 * press it again (or «الكل») to see every unit. Counting rows only — no new figure is computed.
 * «تحتاج مراجعة» counts the server's `needsLook` flag — the same rule that picks units for the AI brief, so
 * a «سليم» unit with red occupancy and open nights is counted too (tasks/unit-performance-ai-brief.md §1).
 * It is a filter as well: a low-occupancy «سليم» unit has no chip of its own to find it by.
 * «نتائج لم تُكتب» counts the server's `changesDue`: changes whose period has ended with «النتيجة» still empty.
 * Pressing it shows those units; the unit opens on the change (tasks/change-impact-before-after.md).
 */
export const NEEDS_REVIEW = 'NEEDS_REVIEW';
// «تنبيه شارة» is a tag, not a verdict — its own chip finds the units that carry it (shown only when one does).
export const BADGE_LOST = 'BADGE_LOST';
export const RESULTS_DUE = 'RESULTS_DUE';

/** The board's row filter for the strip's active pick — a verdict code, «تحتاج مراجعة», «تنبيه شارة» or «نتائج لم تُكتب». */
export const glanceFilter = (pick) => (r) => {
  if (pick === NEEDS_REVIEW) return r.needsLook === true;
  if (pick === BADGE_LOST) return r.badgeLost === true;
  if (pick === RESULTS_DUE) return r.changesDue > 0;
  return r.verdict === pick;
};

const GlanceStrip = ({ rows = [], active, onPick }) => {
  const counts = new Map();
  rows.forEach(r => counts.set(r.verdict, (counts.get(r.verdict) || 0) + 1));
  const codes = VERDICT_ORDER.filter(c => counts.has(c));
  if (!codes.length) return null;
  const attention = rows.filter(glanceFilter(NEEDS_REVIEW)).length;
  const reviewOn = active === NEEDS_REVIEW;
  const badges = rows.filter(glanceFilter(BADGE_LOST)).length;
  const badgeOn = active === BADGE_LOST;
  const resultsDue = rows.reduce((n, r) => n + (r.changesDue || 0), 0);
  const resultsOn = active === RESULTS_DUE;

  return (
    <div className="lp-glance" role="group" aria-label={TEXT.glanceHint}>
      {attention > 0 && (
        <button
          type="button"
          className={`lp-glance__lead${reviewOn ? ' is-active' : ''}`}
          aria-pressed={reviewOn}
          onClick={() => onPick(reviewOn ? null : NEEDS_REVIEW)}
        >
          <i className="bi bi-exclamation-circle" aria-hidden="true" /> {TEXT.glanceAttention}: <bdi className="lp-num">{attention}</bdi>
        </button>
      )}
      {badges > 0 && (
        <button
          type="button"
          className={`lp-glance__chip lp-glance__chip--badge${badgeOn ? ' is-active' : ''}`}
          aria-pressed={badgeOn}
          onClick={() => onPick(badgeOn ? null : BADGE_LOST)}
        >
          <i className="bi bi-award" aria-hidden="true" /> {TEXT.badgeLost} <bdi className="lp-num">{badges}</bdi>
        </button>
      )}
      {resultsDue > 0 && (
        <button
          type="button"
          className={`lp-glance__chip lp-glance__chip--badge${resultsOn ? ' is-active' : ''}`}
          aria-pressed={resultsOn}
          onClick={() => onPick(resultsOn ? null : RESULTS_DUE)}
        >
          <i className="bi bi-pencil-square" aria-hidden="true" /> {TEXT.resultsDue} <bdi className="lp-num">{resultsDue}</bdi>
        </button>
      )}
      <button type="button" className={`lp-glance__chip${!active ? ' is-active' : ''}`} aria-pressed={!active} onClick={() => onPick(null)}>
        {TEXT.glanceAll} <bdi className="lp-num">{rows.length}</bdi>
      </button>
      {codes.map(code => {
        const v = VERDICTS[code] || { label: code, tone: 'neutral' };
        const on = active === code;
        return (
          <button
            key={code}
            type="button"
            className={`lp-glance__chip lp-glance__chip--${v.tone}${on ? ' is-active' : ''}`}
            aria-pressed={on}
            onClick={() => onPick(on ? null : code)}
          >
            {v.label} <bdi className="lp-num">{counts.get(code)}</bdi>
          </button>
        );
      })}
    </div>
  );
};

export default GlanceStrip;
